import * as React from "react";
import { Page } from "src/components/Page";
import { DateNav } from "src/components/DateNav";
import { Instance, Task } from "src/database/types";
import { AnyActionableItem, AnyTaggedItem, TaskGraph, TreeOf } from "src/types";
import { Tree } from "src/utils/Tree";
import { Breadcrumbs } from "./Breadcrumbs";
import { TodayWarning } from "./TodayWarning";
import { TaskTreeEditor } from "../TaskTreeEditor";
import { TaskFocus } from "./Focus/TaskFocus";
import { getLocalDate, parseLocalDate } from "src/database/utils/localDate";
import { useUrlParam } from "src/hooks/useUrlParam";
import { useLiveQuery } from "dexie-react-hooks";
import { getDayPlan } from "src/database/queries/habits";
import { moveItem } from "src/database/queries/items";
import { getBacklog } from "src/database/queries/tasks";
import { GoalAccordion } from "./GoalAccordion";
import { GoalSpotlightProvider } from "./GoalSpotlightContext";
import { shouldHideTask } from "src/utils/shouldHideTask";

export function Planner() {
  const [editing, setEditing] = React.useState(false);
  const [breadcrumb, setBreadcrumb] = React.useState<string[]>([]);
  const [dateStr, setDateStr] = useUrlParam("date");
  const date = parseLocalDate(dateStr);
  const setDate = (date: Date) => setDateStr(getLocalDate(date));
  const initialTasks = useLiveQuery(() => getDayPlan(dateStr), [dateStr]);
  const backlog = useLiveQuery(() => getBacklog());
  const tasks = React.useMemo(() => {
    if (!initialTasks || !backlog) return;
    return tasksToGraph(initialTasks, backlog);
  }, [initialTasks, backlog]);

  const parentId = breadcrumb.length > 0 ? breadcrumb[breadcrumb.length - 1] : null;

  const allFocusTasks = React.useMemo(() => {
    if (!tasks) return [];
    const ids = parentId ? tasks.byParentId[parentId] : tasks.rootIds;
    return Tree.fromGraph(ids, tasks);
  }, [tasks, parentId]);

  const focusTasks = React.useMemo(() => {
    if (!allFocusTasks) return [];
    // don't filter tasks in plan mode
    if (editing) {
      return allFocusTasks;
    }
    const visibleTasks: TreeOf<AnyActionableItem>[] = Tree.filter(allFocusTasks, (task) => {
      return !task.disabled && !shouldHideTask(new Date(date), task.condition);
    });
    return visibleTasks;
  }, [allFocusTasks, editing, date]);

  const allVisibleTasks = React.useMemo(() => {
    if (!tasks) return [];
    const tree = Tree.fromGraph(tasks.rootIds, tasks);
    return Tree.filter(tree, (task) => {
      return !task.disabled && !shouldHideTask(new Date(date), task.condition);
    });
  }, [tasks, date]);

  const onEnterTask = (taskId: string) => {
    setBreadcrumb([...breadcrumb, taskId]);
  };

  return (
    <GoalSpotlightProvider>
      <Page maxWidth={600}>
        <GoalAccordion items={allVisibleTasks} />
        <TodayWarning date={dateStr} />
        <DateNav date={date} setDate={setDate}>
          <DateNav.Link onClick={() => setDate(new Date())}>Today</DateNav.Link>
          <DateNav.Link data-testid="day-planner-mode-btn" onClick={() => setEditing(!editing)}>
            {editing ? "Work Mode" : "Plan Mode"}
          </DateNav.Link>
        </DateNav>
        {tasks && (
          <Breadcrumbs tasks={tasks} breadcrumb={breadcrumb} setBreadcrumb={setBreadcrumb} />
        )}
        {focusTasks && editing && (
          <TaskTreeEditor items={focusTasks} collapse onMoveItem={moveItem} />
        )}
        {focusTasks && !editing && (
          <TaskFocus items={focusTasks} date={dateStr} onEnterTask={onEnterTask} />
        )}
      </Page>
    </GoalSpotlightProvider>
  );
}

function tasksToGraph(instances: Instance[], backlog: Task[]): TaskGraph {
  const graph: TaskGraph = {
    rootIds: [],
    byParentId: {},
    tasks: {},
  };
  for (const instance of instances) {
    const taggedInstance = { ...instance, type: "instance" as const };
    addTaskToGraph(graph, taggedInstance);
    addTaskToParent(graph, taggedInstance);
  }
  for (const task of backlog) {
    addTaskToGraph(graph, task);
    if (task.parentId) {
      addTaskToParent(graph, task);
    } else {
      const folderIds = task.folders ?? [];
      const folderParentIds = instances
        .filter((i) => folderIds.includes(i.templateId))
        .map((i) => i.id);
      if (folderParentIds.length > 0) {
        for (const parentId of folderParentIds) {
          addTaskToParent(graph, task, parentId);
        }
      } else {
        addTaskToParent(graph, task);
      }
    }
  }
  return graph;
}

function addTaskToGraph(graph: TaskGraph, task: AnyActionableItem) {
  graph.tasks[task.id] = task;
}

function addTaskToParent(graph: TaskGraph, task: AnyTaggedItem, overrideParentId?: string | null) {
  const parentId = overrideParentId !== undefined ? overrideParentId : task.parentId;
  if (parentId === null) {
    graph.rootIds.push(task.id);
  } else {
    if (!graph.byParentId[parentId]) {
      graph.byParentId[parentId] = [];
    }
    graph.byParentId[parentId].push(task.id);
  }
}
